import { useState, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, ShieldAlert, CheckSquare, ClipboardList, AlertTriangle, ChevronRight,
  ChevronDown, Shield, BarChart2, ClipboardCheck, Users, CheckSquare2, HelpCircle, PanelLeftClose,
  PanelLeftOpen, UserCircle, FileText, Wrench, CalendarClock, Swords, Building2, Zap,
  ShieldCheck,
} from 'lucide-react';
import clsx from 'clsx';
import { LOGO_ICON, LOGO_REVERSE } from '@/hooks/useThemedLogo';
import { useBrandingStore } from '@/store/branding.store';
import { useSidebarStore } from '@/store/sidebar.store';
import { useAuthStore } from '@/store/auth.store';
import { ADMIN_GROUPS } from '@/config/admin-nav';
import type { UserRole } from '@/types';
import { ResizeHandle } from '@/components/common/ResizeHandle';
import { useResizableWidth } from '@/hooks/useResizableWidth';

/** Roles that may access each nav item. undefined = all roles. */
type NavItemDef = {
  to: string;
  /** Icon colour. Uniformly grey icons made every module look alike. */
  tone?: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  roles?: UserRole[];
};

const mainNavItems: NavItemDef[] = [
  { to: '/dashboard', tone: 'text-sky-500',       label: 'Dashboard',           icon: LayoutDashboard },
  { to: '/risk-workflow', tone: 'text-amber-500',   label: 'Risk Workflow',        icon: Zap,             roles: ['ADMIN', 'RISK_MANAGER', 'COMPLIANCE_OFFICER', 'AUDITOR'] },
  { to: '/risks', tone: 'text-rose-500',           label: 'Risks',               icon: ShieldAlert,     roles: ['ADMIN', 'RISK_MANAGER', 'COMPLIANCE_OFFICER'] },
  { to: '/controls', tone: 'text-emerald-500',     label: 'Controls',            icon: CheckSquare,     roles: ['ADMIN', 'COMPLIANCE_OFFICER', 'AUDITOR'] },
  { to: '/audits', tone: 'text-indigo-500',       label: 'Audits',              icon: ClipboardList,   roles: ['ADMIN', 'AUDITOR', 'COMPLIANCE_OFFICER'] },
  { to: '/findings', tone: 'text-orange-500',     label: 'Findings',            icon: AlertTriangle,   roles: ['ADMIN', 'AUDITOR', 'COMPLIANCE_OFFICER', 'RISK_MANAGER'] },
  { to: '/remediations', tone: 'text-cyan-500', label: 'Remediations',        icon: Wrench,          roles: ['ADMIN', 'COMPLIANCE_OFFICER', 'RISK_MANAGER'] },
  { to: '/frameworks', tone: 'text-violet-500',   label: 'Frameworks',          icon: Shield,          roles: ['ADMIN', 'COMPLIANCE_OFFICER', 'AUDITOR'] },
  { to: '/compliance', tone: 'text-blue-500',   label: 'Compliance',          icon: BarChart2,       roles: ['ADMIN', 'COMPLIANCE_OFFICER', 'AUDITOR'] },
  { to: '/assessments', tone: 'text-teal-500',  label: 'Assessments',         icon: ClipboardCheck,  roles: ['ADMIN', 'COMPLIANCE_OFFICER', 'AUDITOR', 'RISK_MANAGER'] },
  { to: '/tasks', tone: 'text-lime-600',        label: 'Tasks',               icon: CheckSquare2 },
  { to: '/policies', tone: 'text-fuchsia-500',     label: 'Policies',            icon: FileText,        roles: ['ADMIN', 'COMPLIANCE_OFFICER', 'AUDITOR'] },
  { to: '/calendar', tone: 'text-pink-500',     label: 'Calendar',            icon: CalendarClock },
  { to: '/exercises', tone: 'text-red-500',    label: 'Tabletop Exercises',  icon: Swords,          roles: ['ADMIN', 'COMPLIANCE_OFFICER', 'RISK_MANAGER'] },
  { to: '/trust-centre', tone: 'text-green-500', label: 'Trust Centre',        icon: ShieldCheck,     roles: ['ADMIN', 'COMPLIANCE_OFFICER'] },
  { to: '/help', tone: 'text-slate-400',         label: 'Help',                icon: HelpCircle },
];

const clientNavItems: NavItemDef[] = [
  { to: '/admin/clients', tone: 'text-sky-500',            label: 'Clients',            icon: Building2 },
  { to: '/admin/engagements', tone: 'text-indigo-500',        label: 'Engagements',        icon: ClipboardList },
  { to: '/admin/client-assessments', tone: 'text-teal-500', label: 'Client Assessments', icon: ClipboardCheck },
  { to: '/admin/client-exercises', tone: 'text-red-500',   label: 'Client Exercises',   icon: Swords },
  { to: '/admin/client-users', tone: 'text-violet-500',       label: 'Users',              icon: UserCircle },
];

// Administration is five groups (src/config/admin-nav.ts); each entry opens
// the group's first tab and lights up while any tab in the group is open.
const adminNavItems: (NavItemDef & { within: string[] })[] = ADMIN_GROUPS.map(g => ({
  to: g.items[0].to, label: g.label, icon: g.icon, tone: g.tone, within: g.items.map(i => i.to),
}));

interface NavItemProps {
  to: string;
  tone?: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  collapsed: boolean;
  /** Other paths that count as here: a group entry is active on any of its tabs. */
  within?: string[];
}

function NavItem({ to, tone, label, icon: Icon, collapsed, within }: NavItemProps) {
  const { pathname } = useLocation();
  const inGroup = !!within?.some(p => pathname === p || pathname.startsWith(p + '/'));
  return (
    <NavLink
      to={to}
      title={collapsed ? label : undefined}
      className={({ isActive: here }) =>
        clsx(
          'group relative flex items-center rounded text-sm font-medium transition-colors',
          collapsed ? 'justify-center px-2 py-2' : 'gap-3 px-3 py-2',
          (here || inGroup)
            // Selected: navy row, ink-d label and a 3px orange bar on the left.
            ? 'bg-gray-100 text-gray-900 before:absolute before:inset-y-1 before:left-0 before:w-[3px] before:bg-dl-orange'
            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
        )
      }
    >
      {({ isActive: here }) => { const isActive = here || inGroup; return (
        <>
          {/* Module icons are one neutral colour on hull; the active one is orange
              (design system: icons on dark grounds). `tone` is no longer read. */}
          <Icon className={clsx('h-5 w-5 shrink-0 transition-colors', isActive ? 'text-dl-orange' : 'text-gray-400 group-hover:text-gray-700')} />
          {!collapsed && (
            <>
              <span className="truncate">{label}</span>
              {isActive && <ChevronRight className="ml-auto h-4 w-4 text-gray-500" />}
            </>
          )}
          {/* Tooltip shown only in collapsed mode */}
          {collapsed && (
            <span className="pointer-events-none absolute left-14 z-50 whitespace-nowrap rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
              {label}
            </span>
          )}
        </>
      ); }}
    </NavLink>
  );
}

export default function Sidebar() {
  const appName = useBrandingStore(s => s.appName);
  const { collapsed, toggle, mobileOpen, closeMobile } = useSidebarStore();
  const role = useAuthStore((s) => s.user?.role);
  const [clientExpanded, setClientExpanded] = useState(true);
  const [adminExpanded, setAdminExpanded] = useState(true);
  const isAdmin = role === 'ADMIN';
  // Drag the right edge to widen the navigation (remembered per browser).
  const navWidth = useResizableWidth('nav', 256, 200, 360);

  /** Filter nav items based on the current user's role */
  const visibleMainNav = useMemo(
    () => mainNavItems.filter((item) => !item.roles || (role && item.roles.includes(role))),
    [role],
  );

  return (
    <>
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          onClick={closeMobile}
          aria-hidden
        />
      )}
      <aside
        onClick={(e) => {
          // Close mobile drawer when a nav link is clicked.
          if (mobileOpen && (e.target as HTMLElement).closest('a')) closeMobile();
        }}
        className={clsx(
          // `chrome`: the hull navigation ground in both themes (index.css).
          'chrome flex flex-col border-r border-gray-200 bg-surface transition-[width,transform] duration-200 ease-in-out shrink-0',
          // Mobile: fixed-position drawer
          'fixed inset-y-0 left-0 z-40 w-64 md:relative md:translate-x-0',
          mobileOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full',
          // Desktop width — overridden by md: variants
          collapsed ? 'md:w-16' : 'md:w-[var(--nav-w)]',
        )}
        style={{ '--nav-w': `${navWidth.width}px` } as React.CSSProperties}
      >
      {!collapsed && (
        <ResizeHandle label="Navigation width" width={navWidth.width} min={navWidth.min} max={navWidth.max}
          onResize={navWidth.setWidth} onReset={navWidth.reset} className="hidden md:block" />
      )}
      {/* Logo strip, on hull like the rest of the chrome: the reverse logo
          (white lettering, orange double-L), or the square icon when
          collapsed. */}
      <div
        className={clsx(
          'flex h-16 shrink-0 items-center justify-between border-b border-gray-200',
          collapsed ? 'flex-col justify-center gap-1 px-2' : 'px-4',
        )}
      >
        <img
          src={collapsed ? LOGO_ICON : LOGO_REVERSE}
          alt={appName}
          className={clsx('object-contain shrink-0', collapsed ? 'h-7 w-7 rounded-sm' : 'h-9 w-auto max-w-[150px]')}
        />
        <button
          onClick={toggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={clsx(
            'flex shrink-0 items-center justify-center rounded border border-gray-200 text-gray-500 transition-colors hover:border-gray-300 hover:bg-gray-100 hover:text-gray-800',
            collapsed ? 'h-6 w-6' : 'h-8 w-8',
          )}
        >
          {collapsed
            ? <PanelLeftOpen  className="h-4 w-4" />
            : <PanelLeftClose className="h-4 w-4" />}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden px-2 py-3">
        <div className="space-y-0.5">
          {visibleMainNav.map((item) => (
            <NavItem key={item.to} {...item} collapsed={collapsed} />
          ))}
        </div>

        {/* Client Portal section — admin only */}
        {isAdmin && (
        <div className="mt-4">
          {!collapsed ? (
            <button
              onClick={() => setClientExpanded(v => !v)}
              className="mb-1 flex w-full items-center justify-between px-3 ap-label text-gray-400 hover:text-gray-600 transition-colors"
            >
              <span>Client Portal</span>
              <ChevronDown
                className={clsx(
                  'h-3.5 w-3.5 transition-transform duration-200',
                  !clientExpanded && '-rotate-90',
                )}
              />
            </button>
          ) : (
            <div className="my-2 border-t border-gray-200" />
          )}
          {(collapsed || clientExpanded) && (
            <div className="space-y-0.5">
              {clientNavItems.map((item) => (
                <NavItem key={item.to} {...item} collapsed={collapsed} />
              ))}
            </div>
          )}
        </div>
        )}

        {/* System admin section — admin only */}
        {isAdmin && (
        <div className="mt-4">
          {!collapsed ? (
            <button
              onClick={() => setAdminExpanded(v => !v)}
              className="mb-1 flex w-full items-center justify-between px-3 ap-label text-gray-400 hover:text-gray-600 transition-colors"
            >
              <span>Administration</span>
              <ChevronDown
                className={clsx(
                  'h-3.5 w-3.5 transition-transform duration-200',
                  !adminExpanded && '-rotate-90',
                )}
              />
            </button>
          ) : (
            <div className="my-2 border-t border-gray-200" />
          )}
          {(collapsed || adminExpanded) && (
            <div className="space-y-0.5">
              {adminNavItems.map((item) => (
                <NavItem key={item.to} {...item} collapsed={collapsed} />
              ))}
            </div>
          )}
        </div>
        )}
      </nav>

      {/* Bottom: Profile */}
      <div className="border-t border-gray-200 p-2 shrink-0">
        <NavItem to="/profile" label="My Profile" icon={UserCircle} collapsed={collapsed} />
      </div>
    </aside>
    </>
  );
}

